import { Toggle, Dropdown } from '@ets/design-system';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from '_common/hooks';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import { CustomIcon } from '_common/lib/components';

type WopiActionsProps = {
  object: doDOC.File;
};

const WopiActions = ({ object }: WopiActionsProps) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const userId = useSelector((state) => state.auth.userId);

  const CUSTOM_ICONS = {
    Word: <CustomIcon name="Word" size={24} />,
    Excel: <CustomIcon name="Excel" size={24} />,
    PowerPoint: <CustomIcon name="PowerPoint" size={24} />,
  };

  const openInWopi = (action: string, app: string) => {
    const wopiAccess = localStorage.getItem(`${userId}-WOPI_ALLOW_ACCESS`);

    if (wopiAccess) {
      window.open(`/extensions/wopi/${object.id}/${app}/${action}`);
    } else {
      dispatch(
        openAndUpdateModal({
          modal: 'WOPIRedirectionModal',
          data: { app, action, id: object.id },
        }),
      );
    }
  };

  const options = useMemo(() => {
    const options: { app: 'Word' | 'Excel' | 'PowerPoint'; action: string }[] = [];
    const wopiActions = object.wopi?.actions;
    if (wopiActions) {
      wopiActions
        .filter(({ name }) => ['view', 'edit'].includes(name))
        .forEach(({ app, name }) => {
          options.push({ app, action: name });
        });
    }
    return options;
  }, [object]);

  if (options.length > 0) {
    return (
      <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dropdown.Trigger>
          <Toggle
            variant="link"
            size="m"
            iconProps={{ icon: 'ellipsis' }}
            style={{ margin: '0 0 0 0.5rem' }}
            isToggled={isOpen}
            testId="object-preview-wopi"
          />
        </Dropdown.Trigger>
        <Dropdown.Content
          container={document.getElementById('object-preview-root')}
          style={{ width: '42rem' }}
          testId="object-preview-wopi-dropdown"
        >
          {options.map(({ action, app }) => {
            return (
              <Dropdown.Item
                key={`${app}-${action}`}
                onClick={() => openInWopi(action, app)}
                testId={`object-preview-wopi-${action}`}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '0.5rem',
                    alignItems: 'center',
                  }}
                >
                  {app in CUSTOM_ICONS ? CUSTOM_ICONS[app as keyof typeof CUSTOM_ICONS] : undefined}
                  <FormattedMessage
                    id={`WOPI_${action.toUpperCase()}`}
                    values={{ application: app }}
                  />
                </div>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Content>
      </Dropdown.Root>
    );
  }

  return null;
};

export default WopiActions;
